/**
 * WeeklyPlanCard — AI-generated 7-day workout plan with Plateau Breaker.
 * Mounts on Dashboard. Fetches cached plan on load; user can "Generate New" to regenerate.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Sparkles, Loader2, AlertTriangle, TrendingDown, Calendar, RefreshCw } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const INTENSITY_COLOR = {
  low: "#3b82f6",
  moderate: "#CCFF00",
  high: "#FF4500",
  max: "#ef4444",
};

const FOCUS_ICON = {
  Stamina: "🫁",
  Strength: "💪",
  Vertical: "🚀",
  Recovery: "🧘",
};

const WeeklyPlanCard = () => {
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState("");
  const [needsGeneration, setNeedsGeneration] = useState(false);

  const fetchPlan = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API}/weekly-plan`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPlan(res.data.plan);
      setNeedsGeneration(res.data.needs_generation);
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to load plan");
    } finally {
      setLoading(false);
    }
  };

  const generate = async (force = false) => {
    setGenerating(true);
    setError("");
    try {
      const token = localStorage.getItem("token");
      const res = await axios.post(
        `${API}/weekly-plan/generate${force ? "?force=true" : ""}`,
        {},
        { headers: { Authorization: `Bearer ${token}` }, timeout: 120000 }
      );
      setPlan(res.data.plan);
      setNeedsGeneration(false);
    } catch (e) {
      setError(e.response?.data?.detail || "Generation failed");
    } finally {
      setGenerating(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchPlan();
  }, []);

  if (loading) {
    return (
      <div className="bg-[#0A0A0A] border border-white/[0.06] p-6 flex items-center justify-center" data-testid="weekly-plan-loading">
        <Loader2 className="w-5 h-5 text-[#FF4500] animate-spin" />
      </div>
    );
  }

  // Empty state — no plan yet
  if (!plan) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gradient-to-br from-[#0A0A0A] to-[#1a0a05] border border-[#FF4500]/30 p-6 text-center space-y-4"
        data-testid="weekly-plan-empty"
      >
        <div className="inline-flex items-center justify-center w-12 h-12 bg-[#FF4500]/10 border border-[#FF4500]/40">
          <Sparkles className="w-6 h-6 text-[#FF4500]" />
        </div>
        <div>
          <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#FF4500] mb-1">
            AI Coach
          </p>
          <h3 className="text-2xl font-heading text-white uppercase">Your Week, Built For You</h3>
          <p className="text-sm text-white/50 mt-2">
            Generate a personalized 7-day plan based on your recent training, soreness, and goals.
          </p>
        </div>
        <button
          onClick={() => generate(false)}
          disabled={generating}
          className="bg-[#FF4500] hover:bg-[#FF6326] disabled:opacity-50 text-white px-6 py-3 font-heading uppercase tracking-wider text-xs inline-flex items-center gap-2"
          data-testid="generate-weekly-plan-btn"
        >
          {generating ? (
            <><Loader2 className="w-4 h-4 animate-spin" /> Building Plan…</>
          ) : (
            <><Sparkles className="w-4 h-4" /> Generate My Week</>
          )}
        </button>
        {error && <p className="text-xs text-red-400 font-mono" data-testid="weekly-plan-error">{error}</p>}
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-[#0A0A0A] border border-white/[0.06] p-6 space-y-5"
      data-testid="weekly-plan-card"
    >
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <Calendar className="w-3.5 h-3.5 text-[#FF4500]" />
            <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#FF4500]">
              This Week's Plan
            </p>
          </div>
          <p className="text-base text-white" data-testid="weekly-plan-summary">{plan.plan_summary}</p>
        </div>
        <button
          onClick={() => generate(true)}
          disabled={generating}
          className="text-[10px] font-mono uppercase tracking-[0.3em] text-white/40 hover:text-[#FF4500] inline-flex items-center gap-1.5 disabled:opacity-50"
          data-testid="regenerate-weekly-plan-btn"
        >
          {generating ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
          Regen
        </button>
      </div>

      {/* Plateau / Deload alerts */}
      {plan.plateau_detected && (
        <div className="bg-amber-500/10 border border-amber-500/30 p-3 flex items-start gap-2" data-testid="plateau-alert">
          <TrendingDown className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-xs font-mono uppercase tracking-widest text-amber-400 mb-1">Plateau Detected</p>
            <p className="text-xs text-white/70">{plan.plateau_reason || "Your performance has stalled. Plan rotates focus this week."}</p>
          </div>
        </div>
      )}
      {plan.deload_recommended && (
        <div className="bg-blue-500/10 border border-blue-500/30 p-3 flex items-start gap-2" data-testid="deload-alert">
          <AlertTriangle className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-xs font-mono uppercase tracking-widest text-blue-400 mb-1">Deload Week</p>
            <p className="text-xs text-white/70">Soreness signals high. Intensity dialed back across the week for recovery.</p>
          </div>
        </div>
      )}
      {plan.recovery_aware_note && (
        <div className="bg-emerald-500/10 border border-emerald-500/30 p-3 flex items-start gap-2" data-testid="recovery-aware-note">
          <Sparkles className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-xs font-mono uppercase tracking-widest text-emerald-400 mb-1">Recovery-Aware Plan</p>
            <p className="text-xs text-white/70">{plan.recovery_aware_note}</p>
          </div>
        </div>
      )}

      {/* 7-day grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-7 gap-px bg-white/[0.06]">
        {plan.days?.map((d) => (
          <div
            key={d.day}
            className="bg-[#0A0A0A] p-3 space-y-2 min-w-0"
            data-testid={`weekly-plan-day-${d.day}`}
          >
            <div className="flex items-center justify-between">
              <p className="text-[9px] font-mono uppercase tracking-[0.2em] text-white/40">
                {d.label?.slice(0, 3) || `D${d.day}`}
              </p>
              <span
                className="text-[8px] font-mono uppercase tracking-widest px-1.5 py-0.5"
                style={{
                  background: `${INTENSITY_COLOR[d.intensity] || '#666'}22`,
                  color: INTENSITY_COLOR[d.intensity] || '#999',
                }}
              >
                {d.intensity}
              </span>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="text-base">{FOCUS_ICON[d.focus] || "🏀"}</span>
              <p className="text-xs text-white font-semibold truncate">{d.focus}</p>
            </div>
            <p className="text-[9px] font-mono text-white/30">{d.duration_min || 30}m</p>
            <ul className="space-y-0.5">
              {d.drills?.slice(0, 3).map((drill, i) => (
                <li key={i} className="text-[10px] text-white/60 truncate">· {drill}</li>
              ))}
              {d.drills && d.drills.length > 3 && (
                <li className="text-[10px] text-white/30">+{d.drills.length - 3} more</li>
              )}
            </ul>
            {d.coach_note && (
              <p className="text-[10px] text-[#FF4500]/70 italic line-clamp-2">{d.coach_note}</p>
            )}
          </div>
        ))}
      </div>

      {/* Tips */}
      {plan.weekly_focus_tip && (
        <div className="border-t border-white/[0.06] pt-3 space-y-1.5">
          <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-white/30">Coach Tip</p>
          <p className="text-sm text-white" data-testid="weekly-plan-tip">{plan.weekly_focus_tip}</p>
          {plan.next_week_preview && (
            <p className="text-xs text-white/40 mt-2">↗ Next week: {plan.next_week_preview}</p>
          )}
        </div>
      )}

      {needsGeneration && (
        <p className="text-[10px] font-mono text-white/30 text-center">
          Plan is stale ({plan.age_hours || 24}+ hours old) — tap Regen for fresh
        </p>
      )}
    </motion.div>
  );
};

export default WeeklyPlanCard;
